import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { XIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'
import { veilCloses } from '../../lib/veil'
import type { FilterArt } from '../../lib/chatfilter'
import { useTexte } from '../../texte'

/**
 * Which part of the chat to show (1.93.0, #114) -- behind the funnel in the
 * header.
 *
 * Built like `Tagestrennerliste`: a sheet on `document.body`, one tap each,
 * the current choice marked with `aria-current`. **And the veil closes on a
 * tap** (`bogen--schliesst`, `veilCloses()`) -- a choice of three that only
 * closes by the cross would be one step too many. Since 1.126.0 the lists of
 * days and of polls do the same; until then only this sheet did, and a second
 * list could be opened through the veil of the first.
 */
export function Chatfilter({
  arten,
  aktiv,
  onWahl,
  onSchliessen,
}: {
  /** The filters this chat offers; "all" always stands first. */
  arten: FilterArt[]
  aktiv: FilterArt | null
  onWahl: (art: FilterArt | null) => void
  onSchliessen: () => void
}) {
  const texte = useTexte()
  const t = texte.chat.filter

  useEscape(onSchliessen)
  // The keyboard goes down: the sheet is a choice, not a place to type.
  useEffect(() => {
    const feld = document.activeElement
    if (feld instanceof HTMLElement) feld.blur()
  }, [])

  const wahlen: (FilterArt | null)[] = [null, ...arten]

  return createPortal(
    <div
      className="bogen bogen--obenauf bogen--schliesst"
      role="dialog"
      aria-modal="true"
      aria-label={t.titel}
      data-chatfilter-blatt=""
      onClick={veilCloses(onSchliessen)}
    >
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{t.titel}</h3>
          <button type="button" className="icon-button" onClick={onSchliessen} aria-label={texte.allgemein.schliessen} title={texte.allgemein.schliessen}>
            <XIcon size={18} />
          </button>
        </div>
        <div className="bogen__rolle">
          <ul className="tageswahl__liste">
            {wahlen.map((art) => (
              <li key={art ?? 'alle'}>
                <button
                  type="button"
                  className="tageswahl__zeile"
                  aria-current={art === aktiv ? 'true' : undefined}
                  data-chatfilter-wahl={art ?? 'alle'}
                  onClick={() => onWahl(art)}
                >
                  <span>{art === null ? t.alle : art === 'beitraege' ? t.beitraege : t.privat}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body,
  )
}
